import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../icon/icon';
import * as s from './styled-variable-linker';
import {
  isAbortError,
  notifyError,
  notifySuccess,
  useDebounce,
} from '../../utils';
import {
  getAllVariablesAdmin,
  getVariablesFiltered,
  linkThingVariables,
} from '../../services';

// Extrai label de tipo da variavel (relacao VariableType ou string).
const extractTypeLabel = (variable) => {
  const t = variable?.type;
  if (!t) return '';
  if (typeof t === 'string') return t;
  return t.name || t.label || t.type || '';
};

// Parser CSV minimalista: 1 ihmname por linha. Aceita header opcional
// ("ihmname" na primeira linha), aspas pra envolver valores com
// virgula (raro mas defensivo), ignora linhas vazias. Caso o CSV tenha
// varias colunas, pega so a primeira.
const parseIhmnameCsv = (text) => {
  if (!text) return [];
  const lines = text.split(/\r?\n/);
  const out = [];
  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim();
    if (!line) return;
    // Pega primeira coluna respeitando aspas.
    let firstCol = '';
    if (line.startsWith('"')) {
      const end = line.indexOf('"', 1);
      firstCol = end > 0 ? line.slice(1, end) : line.slice(1);
    } else {
      const comma = line.indexOf(',');
      firstCol = comma > 0 ? line.slice(0, comma) : line;
    }
    firstCol = firstCol.trim();
    if (!firstCol) return;
    // Skip header se a primeira linha disser "ihmname" / "name".
    if (idx === 0) {
      const low = firstCol.toLowerCase();
      if (low === 'ihmname' || low === 'name' || low === 'código') return;
    }
    out.push(firstCol);
  });
  return out;
};

// Gera template CSV simples. Header + 3 exemplos placeholder.
const buildTemplateCsv = () =>
  [
    'ihmname',
    'TEMP_SENSOR_01',
    'CMD_Liga',
    'Sup_PotenciaAtivaMedia',
  ].join('\n');

// Dispara download de string como arquivo. Sem dep externa — usa Blob +
// link sintetico + click. Cleanup do object URL apos pequeno delay.
const downloadTextAsFile = (text, filename) => {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
};

const VariableLinker = ({ thingId, onSaved }) => {
  // catalogo: TODAS as variaveis do sistema. Carrega 1x ao montar.
  const [catalog, setCatalog] = useState([]);
  // linkedIds: Set dos ids atualmente vinculados (estado editavel).
  const [linkedIds, setLinkedIds] = useState(() => new Set());
  // Snapshot dos ids vinculados quando o modal abriu — usado pra
  // calcular diff visual (X adicionadas / Y removidas).
  const [initialLinkedIds, setInitialLinkedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Busca textual aplica nos 2 paineis simultaneamente. Debounce evita
  // filtrar a cada keystroke quando o catalogo tem 500+ items.
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 200);

  // Chips de filtro de tipo (multi-select OR). Vazio = todos.
  const [activeTypeFilters, setActiveTypeFilters] = useState(() => new Set());

  // Selecao em cada painel pra bulk-move via botoes. Set de ids.
  const [selectedAvailable, setSelectedAvailable] = useState(() => new Set());
  const [selectedLinked, setSelectedLinked] = useState(() => new Set());

  // Resumo do ultimo import CSV. null = nenhum import recente. Quando
  // setado, exibe painel inline com counts + lista expansivel dos
  // "nao encontrados".
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  // Carga inicial: catalogo + ids vinculados atuais.
  useEffect(() => {
    if (!thingId) return undefined;
    const controller = new AbortController();
    let mounted = true;

    setLoading(true);
    Promise.all([
      getAllVariablesAdmin(),
      // limit alto pra trazer todas as ranges desta thing de uma vez.
      // Backend agora tem the two-query strategy + indexes, entao e rapido.
      getVariablesFiltered({
        thingId,
        limit: 9999,
        page: 1,
        signal: controller.signal,
      }),
    ])
      .then(([catalogResp, linkedResp]) => {
        if (!mounted) return;
        // Resposta pode vir como [{...}], {data: [...]}, ou {data: {data: [...]}}
        // dependendo do envelope do endpoint. Tenta na ordem.
        const pickArray = (resp) => {
          if (Array.isArray(resp)) return resp;
          if (resp && Array.isArray(resp.data)) return resp.data;
          if (resp && resp.data && Array.isArray(resp.data.data))
            return resp.data.data;
          return [];
        };
        const catalogData = pickArray(catalogResp);
        const linkedArr = pickArray(linkedResp);
        const linkedSet = new Set(linkedArr.map((v) => v.id));
        setCatalog(catalogData);
        setLinkedIds(linkedSet);
        setInitialLinkedIds(new Set(linkedSet));
      })
      .catch((err) => {
        if (!mounted) return;
        if (isAbortError(err)) return;
        notifyError('Erro ao carregar variáveis');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [thingId]);

  // Tipos distintos no catalogo — popula os chips do filtro.
  const availableTypes = useMemo(() => {
    const set = new Set();
    catalog.forEach((v) => {
      const t = extractTypeLabel(v);
      if (t) set.add(t);
    });
    return Array.from(set).sort();
  }, [catalog]);

  // Predicado unico aplicado em ambos os paineis. Busca por ihmname ou
  // name (descricao legivel) — case-insensitive.
  const matchesFilters = (variable) => {
    const term = (debouncedSearch || '').trim().toLowerCase();
    if (term) {
      const hay = `${variable.ihmname || ''} ${variable.name || ''}`.toLowerCase();
      if (!hay.includes(term)) return false;
    }
    if (activeTypeFilters.size > 0) {
      const t = extractTypeLabel(variable);
      if (!activeTypeFilters.has(t)) return false;
    }
    return true;
  };

  // Particiona catalogo em (disponiveis, vinculadas) — filtros sao
  // aplicados antes da particao pra evitar percorrer 2x.
  const { availableList, linkedList } = useMemo(() => {
    const av = [];
    const lk = [];
    catalog.forEach((v) => {
      if (!matchesFilters(v)) return;
      if (linkedIds.has(v.id)) lk.push(v);
      else av.push(v);
    });
    const cmp = (a, b) =>
      (a.ihmname || a.name || '').localeCompare(b.ihmname || b.name || '');
    av.sort(cmp);
    lk.sort(cmp);
    return { availableList: av, linkedList: lk };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, linkedIds, debouncedSearch, activeTypeFilters]);

  // Diff vs estado inicial — exibido no rodape pra feedback do operador.
  const diff = useMemo(() => {
    let added = 0;
    let removed = 0;
    linkedIds.forEach((id) => {
      if (!initialLinkedIds.has(id)) added += 1;
    });
    initialLinkedIds.forEach((id) => {
      if (!linkedIds.has(id)) removed += 1;
    });
    return { added, removed, totalLinked: linkedIds.size };
  }, [linkedIds, initialLinkedIds]);

  const hasChanges = diff.added > 0 || diff.removed > 0;

  // === acoes ===
  const toggleTypeFilter = (type) => {
    setActiveTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  // Click simples seleciona/desseleciona o item (toggle). Mantemos
  // selecoes separadas por painel — o operador pode escolher varios em
  // cada lado e o botao apropriado aparece habilitado.
  const toggleSelectAvailable = (id) => {
    setSelectedAvailable((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectLinked = (id) => {
    setSelectedLinked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Double-click move uma variavel direto pro outro lado. Atalho rapido
  // pro caso comum (mover 1 item). Para multiplos, use botoes setas.
  const moveOneToLinked = (id) => {
    setLinkedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setSelectedAvailable((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };
  const moveOneToAvailable = (id) => {
    setLinkedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSelectedLinked((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // Bulk: move tudo que esta selecionado no painel de origem.
  const moveSelectedToLinked = () => {
    if (selectedAvailable.size === 0) return;
    setLinkedIds((prev) => {
      const next = new Set(prev);
      selectedAvailable.forEach((id) => next.add(id));
      return next;
    });
    setSelectedAvailable(new Set());
  };
  const moveSelectedToAvailable = () => {
    if (selectedLinked.size === 0) return;
    setLinkedIds((prev) => {
      const next = new Set(prev);
      selectedLinked.forEach((id) => next.delete(id));
      return next;
    });
    setSelectedLinked(new Set());
  };

  // === CSV import / export ===
  const handleDownloadTemplate = () => {
    downloadTextAsFile(buildTemplateCsv(), 'variaveis-template.csv');
  };

  // Abre o file picker (input escondido por estilo). O click no input
  // direto seria possivel mas o ref permite resetar o value depois pra
  // permitir re-import do mesmo arquivo.
  const triggerImport = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset do input pra permitir selecionar o mesmo arquivo novamente.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const ihmnames = parseIhmnameCsv(text);
      if (ihmnames.length === 0) {
        notifyError('CSV vazio ou sem ihmnames válidos.');
        return;
      }
      // Indexa catalogo por ihmname (case-insensitive) pra match rapido.
      const byIhmname = new Map();
      catalog.forEach((v) => {
        if (v.ihmname) byIhmname.set(v.ihmname.toLowerCase(), v);
      });

      const matched = [];
      const alreadyLinked = [];
      const notFound = [];
      ihmnames.forEach((raw) => {
        const v = byIhmname.get(raw.toLowerCase());
        if (!v) {
          notFound.push(raw);
          return;
        }
        if (linkedIds.has(v.id)) {
          alreadyLinked.push(v);
        } else {
          matched.push(v);
        }
      });

      // Adiciona os novos ao state (merge — nao substitui). Operador
      // ainda precisa clicar SAVE pra persistir.
      if (matched.length > 0) {
        setLinkedIds((prev) => {
          const next = new Set(prev);
          matched.forEach((v) => next.add(v.id));
          return next;
        });
      }

      const variant =
        notFound.length > 0 && matched.length === 0
          ? 'error'
          : notFound.length > 0
          ? 'warn'
          : 'ok';
      setImportSummary({
        total: ihmnames.length,
        matched: matched.length,
        alreadyLinked: alreadyLinked.length,
        notFound,
        variant,
      });
    } catch (err) {
      notifyError('Erro ao ler arquivo CSV.');
    }
  };

  const handleSave = async () => {
    if (!thingId) return;
    setSaving(true);
    try {
      const ids = Array.from(linkedIds);
      const resp = await linkThingVariables(thingId, ids);
      const result = (resp && resp.data) || resp || {};
      // Re-baseia o snapshot pos-save — diff zera.
      setInitialLinkedIds(new Set(linkedIds));
      notifySuccess('Atualizado com sucesso.');
      if (typeof onSaved === 'function') onSaved(result);
    } catch (err) {
      notifyError(err, 'Erro ao salvar variáveis vinculadas.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <s.LoadingState>Carregando variáveis…</s.LoadingState>;
  }

  return (
    <s.Container>
      <s.Toolbar>
        <s.SearchInput
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Buscar variável (nome ou descrição)…"
        />
        {availableTypes.slice(0, 8).map((type) => (
          <s.TypeChip
            key={type}
            type="button"
            $active={activeTypeFilters.has(type)}
            onClick={() => toggleTypeFilter(type)}
            title={`Filtrar por ${type}`}
          >
            {type}
          </s.TypeChip>
        ))}
        <s.ToolbarButton
          type="button"
          onClick={handleDownloadTemplate}
          title="Baixar modelo CSV com header e exemplos"
        >
          <Icon name="download" size={14} />
          Baixar Modelo
        </s.ToolbarButton>
        <s.ToolbarButton
          type="button"
          onClick={triggerImport}
          title="Importar CSV de ihmnames pra vincular em lote"
        >
          <Icon name="upload" size={14} />
          Importar CSV
        </s.ToolbarButton>
        <s.HiddenFileInput
          ref={fileInputRef}
          onChange={handleFileSelected}
        />
        <s.HintText>Click duplo move • Click simples seleciona</s.HintText>
      </s.Toolbar>

      {importSummary && (
        <s.ImportSummaryPanel $variant={importSummary.variant}>
          <s.ImportSummaryBody>
            <strong>
              CSV processado: {importSummary.total} linha(s)
            </strong>
            <s.ImportSummaryRow>
              ✓ {importSummary.matched} adicionada(s) à lista (revise e
              salve)
              {importSummary.alreadyLinked > 0 && (
                <> · {importSummary.alreadyLinked} já estavam vinculadas</>
              )}
            </s.ImportSummaryRow>
            {importSummary.notFound.length > 0 && (
              <s.NotFoundList>
                <summary>
                  {importSummary.notFound.length} ihmname(s) não
                  encontrado(s) no catálogo
                </summary>
                <div style={{ marginTop: 6 }}>
                  {importSummary.notFound.map((n) => (
                    <s.NotFoundCode key={n}>{n}</s.NotFoundCode>
                  ))}
                </div>
              </s.NotFoundList>
            )}
          </s.ImportSummaryBody>
          <s.ImportSummaryClose
            type="button"
            onClick={() => setImportSummary(null)}
            title="Fechar resumo"
          >
            <Icon name="x" size={16} />
          </s.ImportSummaryClose>
        </s.ImportSummaryPanel>
      )}

      <s.Panes>
        <s.Pane>
          <s.PaneHeader>
            <span>Variáveis Disponíveis</span>
            <s.CountBadge>{availableList.length}</s.CountBadge>
          </s.PaneHeader>
          <s.PaneList>
            {availableList.length === 0 ? (
              <s.EmptyPane>
                {debouncedSearch || activeTypeFilters.size
                  ? 'Nenhuma variável corresponde aos filtros.'
                  : 'Todas as variáveis já estão vinculadas.'}
              </s.EmptyPane>
            ) : (
              availableList.map((v) => (
                <s.ListItem
                  key={v.id}
                  $selected={selectedAvailable.has(v.id)}
                  onClick={() => toggleSelectAvailable(v.id)}
                  onDoubleClick={() => moveOneToLinked(v.id)}
                  title={v.name || ''}
                >
                  <s.ListItemName>{v.ihmname || v.name}</s.ListItemName>
                  <s.TypeTag type={extractTypeLabel(v)}>
                    {extractTypeLabel(v) || '—'}
                  </s.TypeTag>
                </s.ListItem>
              ))
            )}
          </s.PaneList>
        </s.Pane>

        <s.MoveButtonsCol>
          <s.MoveButton
            type="button"
            disabled={selectedAvailable.size === 0}
            onClick={moveSelectedToLinked}
            title={`Vincular ${selectedAvailable.size} selecionada(s)`}
          >
            <Icon name="chevron-right" size={18} />
          </s.MoveButton>
          <s.MoveButton
            type="button"
            disabled={selectedLinked.size === 0}
            onClick={moveSelectedToAvailable}
            title={`Desvincular ${selectedLinked.size} selecionada(s)`}
          >
            <Icon name="chevron-left" size={18} />
          </s.MoveButton>
        </s.MoveButtonsCol>

        <s.Pane>
          <s.PaneHeader>
            <span>Variáveis Vinculadas</span>
            <s.CountBadge>{linkedList.length}</s.CountBadge>
          </s.PaneHeader>
          <s.PaneList>
            {linkedList.length === 0 ? (
              <s.EmptyPane>
                {debouncedSearch || activeTypeFilters.size
                  ? 'Nenhuma vinculada corresponde aos filtros.'
                  : 'Nenhuma variável vinculada a esta máquina.'}
              </s.EmptyPane>
            ) : (
              linkedList.map((v) => (
                <s.ListItem
                  key={v.id}
                  $selected={selectedLinked.has(v.id)}
                  onClick={() => toggleSelectLinked(v.id)}
                  onDoubleClick={() => moveOneToAvailable(v.id)}
                  title={v.name || ''}
                >
                  <s.ListItemName>{v.ihmname || v.name}</s.ListItemName>
                  <s.TypeTag type={extractTypeLabel(v)}>
                    {extractTypeLabel(v) || '—'}
                  </s.TypeTag>
                </s.ListItem>
              ))
            )}
          </s.PaneList>
        </s.Pane>
      </s.Panes>

      <s.Footer>
        <s.DiffSummary>
          {hasChanges ? (
            <>
              {diff.added > 0 && <strong>+{diff.added} a adicionar </strong>}
              {diff.removed > 0 && (
                <strong>−{diff.removed} a remover </strong>
              )}
              · Total final: {diff.totalLinked}
            </>
          ) : (
            <>Nenhuma alteração pendente · {diff.totalLinked} vinculada(s)</>
          )}
        </s.DiffSummary>
        <s.SaveButton
          type="button"
          disabled={!hasChanges || saving}
          onClick={handleSave}
        >
          {saving ? 'SALVANDO…' : 'SALVAR VARIÁVEIS'}
        </s.SaveButton>
      </s.Footer>
    </s.Container>
  );
};

export default VariableLinker;
